import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GridSortDirection } from '@mui/x-data-grid';

import { TableState } from '@app/ui/table/types/TableState';

export interface RuleListFilter {
  search?: string;
  severity?: string;
  type?: string;
}

export type RuleListTableState = TableState<RuleListFilter>;

const DEFAULT_SORT_FIELD = 'ruleId';
const DEFAULT_SORT_DIRECTION: GridSortDirection = 'asc';
const DEFAULT_LIMIT = 30;

const parseOrderBy = (sort: string | null): RuleListTableState['orderBy'] => {
  if (sort === 'none') {
    return [];
  }

  const [field, direction] = sort ? sort.split(':') : [];

  return [
    { field: field || DEFAULT_SORT_FIELD, direction: (direction || DEFAULT_SORT_DIRECTION) as GridSortDirection },
  ];
};

const parseState = (searchParams: URLSearchParams): RuleListTableState => {
  const page = Number(searchParams.get('page'));
  const limit = Number(searchParams.get('limit'));

  return {
    page: page > 0 ? page : 1,
    limit: limit > 0 ? limit : DEFAULT_LIMIT,
    orderBy: parseOrderBy(searchParams.get('sort')),
    search: searchParams.get('search') ?? undefined,
    severity: searchParams.get('severity') ?? 'all',
    type: searchParams.get('type') ?? 'all',
  };
};

const serializeState = (state: RuleListTableState, previous: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(previous);
  const [sort] = state.orderBy;

  next.set('page', String(state.page));
  next.set('limit', String(state.limit));
  next.set('sort', sort ? `${sort.field}:${sort.direction ?? 'asc'}` : 'none');
  next.set('severity', state.severity ?? 'all');
  next.set('type', state.type ?? 'all');

  if (state.search) {
    next.set('search', state.search);
  } else {
    next.delete('search');
  }

  return next;
};

// The editor is a sibling ROUTE, so opening a rule UNMOUNTS `RuleList` and any `useState`-held page/
// sort/search/filter is lost on the round trip. The URL survives that, a reload, and is shareable.
// `replace: true` keeps every paginator tweak out of browser history.
export const useRuleListTableState = (): [RuleListTableState, (newState: Partial<RuleListTableState>) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseState(searchParams), [searchParams]);

  const onChangeState = useCallback(
    (newState: Partial<RuleListTableState>) => {
      setSearchParams(previous => serializeState({ ...parseState(previous), ...newState }, previous), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  return [state, onChangeState];
};
